import { Text } from '@noorden-insurtech/alba-design-system';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useState } from 'react';

type Progress = {
  rated: number;
  pending: number;
  total: number;
  percent: number;
};

/**
 * Mesmo widget flutuante da importação de vidas, para o recálculo da análise.
 * Mostra quantas vidas já foram re-tarifadas e quantas ainda faltam.
 */
export function RecalculationProgressOverlay({
  progress,
  error,
}: {
  progress: Progress;
  error?: string;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const livesKnown = progress.total > 0;
  const failed = Boolean(error);
  const consolidating = livesKnown && progress.pending === 0;
  const headline = failed
    ? 'Re-tarifação interrompida'
    : consolidating
      ? 'Consolidando o prêmio…'
      : 'Re-tarifando vidas…';
  const subhead = failed
    ? error
    : consolidating
      ? 'As vidas já foram re-tarifadas. O total da cotação está sendo fechado.'
      : livesKnown
        ? `Faltam ${progress.pending} vida(s).`
        : 'Preparando a re-tarifação das vidas.';

  if (collapsed) {
    return (
      <button
        type='button'
        onClick={() => setCollapsed(false)}
        className={`fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full border border-alba-gray-200 border-l-4 ${failed ? 'border-l-alba-error-red-500' : 'border-l-alba-blue-500'} bg-alba-white-500 px-3 py-2 shadow-lg transition hover:shadow-xl`}
        aria-label='Expandir status do recálculo'
      >
        {failed ? (
          <AlertTriangle
            className='h-5 w-5 text-alba-error-red-500'
            strokeWidth={1.8}
          />
        ) : (
          <Loader2
            className='h-5 w-5 animate-spin text-alba-blue-500'
            strokeWidth={1.8}
          />
        )}
        <Text className='text-xs font-semibold text-alba-black-500'>
          {failed
            ? 'Erro na re-tarifação'
            : livesKnown
              ? `${progress.rated}/${progress.total}`
              : 'Re-tarifando…'}
        </Text>
        <ChevronUp className='h-4 w-4 text-alba-gray-500' strokeWidth={1.8} />
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-4 right-4 z-40 flex w-[360px] flex-col gap-3 rounded-2xl border border-alba-gray-200 border-l-4 ${failed ? 'border-l-alba-error-red-500' : 'border-l-alba-blue-500'} bg-alba-white-500 p-4 shadow-lg`}
      role='status'
      aria-live='polite'
    >
      <div className='flex items-start gap-3'>
        {failed ? (
          <AlertTriangle
            className='mt-0.5 h-5 w-5 shrink-0 text-alba-error-red-500'
            strokeWidth={1.8}
          />
        ) : (
          <Loader2
            className='mt-0.5 h-5 w-5 shrink-0 animate-spin text-alba-blue-500'
            strokeWidth={1.8}
          />
        )}
        <div className='flex-1'>
          <Text className='text-sm font-bold text-alba-black-500'>
            {headline}
          </Text>
          <Text className='mt-0.5 text-xs text-alba-gray-600'>{subhead}</Text>
        </div>
        <button
          type='button'
          onClick={() => setCollapsed(true)}
          className='rounded p-1 hover:bg-black/5'
          aria-label='Minimizar widget de recálculo'
        >
          <ChevronDown
            className='h-4 w-4 text-alba-gray-500'
            strokeWidth={1.8}
          />
        </button>
      </div>

      {livesKnown && !failed && (
        <div className='flex flex-col gap-1.5'>
          <div className='flex items-center justify-between'>
            <Text className='text-xs text-alba-gray-600'>
              {progress.rated} de {progress.total} re-tarifadas
            </Text>
            <Text className='text-xs font-semibold text-alba-black-500'>
              {progress.percent}%
            </Text>
          </div>
          <div
            className='h-1.5 w-full overflow-hidden rounded-full bg-alba-gray-200'
            role='progressbar'
            aria-valuenow={progress.percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className='h-full rounded-full bg-alba-blue-500 transition-[width] duration-500 ease-out'
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
